<template>
  <div
    class="lg:bg-gray-lighter [&_img]:inline-block [&_svg]:inline-block"
    :class="isCartRoute ? 'bg-white' : 'bg-gray-lighter'"
  >
    <!-- This makes the receipt page its own thing so the rest of the checkout flow doesn't have to know about it -->
    <template v-if="$route.name === 'checkout-receipt-id'">
      <NuxtPage />
    </template>

    <template v-else>
      <div class="p-3 lg:container">
        <template v-if="!cart.isInitialized.value">
          <div class="py-10">
            <SpinnerText text="Cart Loading" is-dark />
          </div>
        </template>

        <template v-else>
          <ul
            class="flex justify-between mt-2 text-gray text-sm whitespace-nowrap font-bold sm:text-lg md:text-xl lg:justify-start lg:gap-x-10 lg:mt-8 lg:mb-12"
          >
            <li class="or-checkout-step-active hover:underline">
              <UniversalLink link="/checkout/cart/" class="flex items-center">
                <span class="or-checkout-step-box bg-gray text-light w-6 mr-2 rounded text-center leading-6">1</span>
                Cart
              </UniversalLink>
            </li>

            <li :class="{ 'or-checkout-step-active hover:underline': !isCartRoute }">
              <UniversalLink
                :link="$route.name === 'checkout-payment' ? '/checkout/address/' : ''"
                class="flex items-center"
              >
                <span class="or-checkout-step-box bg-gray text-light w-6 mr-2 rounded text-center leading-6">2</span>
                Address
              </UniversalLink>
            </li>

            <li :class="{ 'or-checkout-step-active hover:underline': $route.name === 'checkout-payment' }">
              <div class="flex items-center">
                <span class="or-checkout-step-box bg-gray text-light w-6 mr-2 rounded text-center leading-6">3</span>
                Payment
              </div>
            </li>
          </ul>

          <p id="cart" class="my-5 capitalize text-3xl font-bold">
            <template v-if="$route.name === 'checkout-cart'">
              <span class="flex flex-col lg:flex-row lg:items-center"> Shopping Cart </span>
            </template>
            <template v-else-if="$route.name === 'checkout-address'"> Shipping Address </template>

            <template v-else-if="$route.name === 'checkout-payment'">
              <template
                v-if="cart.state.value.summary.giftCardSubtotal !== 0 && cart.state.value.summary.grandTotal === 0"
              >
                Checkout With Gift Card
              </template>
              <template v-else> Checkout With Credit Card </template>
            </template>
          </p>

          <template v-if="cart.state.value.itemCount === 0">
            <p class="pb-6">There are no items in your cart</p>
            <div v-if="isIntegrationEnabled('cognito')">
              <Wishlist empty-cart-mode>
                <template #default>
                  <div class="my-4 capitalize text-3xl font-bold">Saved Items</div>
                </template>
              </Wishlist>
            </div>
          </template>

          <template v-if="cart.state.value.itemCount > 0">
            <div class="lg:flex lg:justify-between">
              <div class="bg-gray-lighter mb-5" :class="checkoutWrapperClass">
                <NuxtPage />
              </div>

              <div :class="isCartRoute ? 'lg:w-[30%] lg:max-w-[400px]' : 'lg:w-2/5'">
                <CheckoutSubtotal id="subtotal" />
              </div>
            </div>
          </template>
        </template>
      </div>

      <template v-if="isCartRoute && !$device.value.isDesktop">
        <CheckoutCartRecommendations v-if="inExperiment && firstCartItem" :item-id="firstCartItem" />
        <div class="p-3 bg-gray-lighter lg:container">
          <CheckoutNeedHelp />
        </div>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
const { $extend } = useNuxtApp()
const { isIntegrationEnabled } = useUtils()
const route = useRoute()
const cart = useCart()
const amplitude = useAmplitude()

const inExperiment = ref(false)
// A/B test for Cart Recommendations UWP-13964
amplitude.onReady({
  experimentId: 'cart-rec-tray',
  callback: (variant) => {
    if (variant.value !== 'treatment') return
    inExperiment.value = true
  },
})

const isCartRoute = computed(() => {
  return route.name === 'checkout-cart'
})

const checkoutWrapperClass = computed(() => {
  if (!isCartRoute.value) return 'lg:w-1/2 lg:grow-0'
  return 'lg:w-[67%]'
})

const filteredCartItems = computed(() => {
  return !isIntegrationEnabled('extend')
    ? cart.state.value.items
    : $extend.getCartItemsExcludingShippingProtection(cart.state.value.items)
})

const firstCartItem = computed(() => {
  return filteredCartItems.value[0]?.productLineSlug
})
</script>
